/**
 * The betslip — the core screen (spec §7).
 *
 * Every requirement in the spec's betslip list is a reaction to something the
 * competitor gets wrong, so each is called out where it is implemented:
 *
 *   - crowd-pick percentages instead of odds
 *   - the full prize ladder, including consolation rungs, before purchase
 *   - one auto-pick button
 *   - a countdown in mm:ss that actually moves
 *   - a live pool total
 *   - a multiplier selector on the base unit
 *   - never a static "Win 100,000"
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, type RoundDetail } from './api.js';
import { rememberTicket } from './MyTickets.js';
import { formatCountdown, formatTzs, parseSenti } from './money.js';

type Pick = 'HOME' | 'DRAW' | 'AWAY';

export function Betslip({ round }: { round: RoundDetail }): React.JSX.Element {
  const [picks, setPicks] = useState<readonly (Pick | null)[]>(() =>
    Array.from({ length: round.legs }, () => null),
  );
  const [multiplier, setMultiplier] = useState(round.allowedMultipliers[0] ?? 1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const secondsRemaining = useCountdown(round.entryCutoffAt);
  const complete = picks.every((p) => p !== null);
  const stakeSenti = parseSenti(round.baseUnitSenti) * BigInt(multiplier);

  const setLeg = useCallback((index: number, value: Pick) => {
    setPicks((prev) => prev.map((p, i) => (i === index ? value : p)));
  }, []);

  const autoPick = useCallback(async () => {
    // Spec §7: "One auto-pick button, not two doing the same job."
    const { picks: suggested } = await api.quickPick(round.roundKey);
    setPicks(suggested.slice(0, round.legs) as readonly Pick[]);
  }, [round.roundKey, round.legs]);

  const submit = useCallback(async () => {
    if (!complete || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.buyTicket(round.roundKey, picks as readonly string[], multiplier);
      // Remembered locally so the My Tickets screen can find it. The server is
      // the record; this is only an index into it.
      rememberTicket({ ticketId: result.ticketId, roundId: round.roundKey });
      setConfirmed(true);
      setPicks(Array.from({ length: round.legs }, () => null));
    } catch (err) {
      // The API's refusal messages are written for a customer to read —
      // 'your weekly spend cap has been reached' is more useful than a generic
      // failure, and hiding it would make a deliberate limit look like a bug.
      setError(err instanceof ApiError ? err.message : 'Imeshindikana. Jaribu tena.');
    } finally {
      setBusy(false);
    }
  }, [complete, busy, round.roundKey, picks, multiplier]);

  return (
    <main style={S.page}>
      <header style={S.header}>
        {/* Never "Win 100,000". Always the live pool, labelled progressive. */}
        <div style={S.poolLabel}>Jackpot sasa</div>
        <div style={S.pool}>{formatTzs(round.prizePoolSenti)} TZS</div>
        <div style={S.poolNote}>
          Inaongezeka kila tiketi · {round.ticketCount} tiketi
          {parseSenti(round.rolloverInSenti) > 0n
            ? ` · imejumuisha ${formatTzs(round.rolloverInSenti)} TZS iliyobaki`
            : ''}
        </div>
        <div style={secondsRemaining < 300 ? S.countdownUrgent : S.countdown}>
          {secondsRemaining > 0 ? formatCountdown(secondsRemaining) : 'Imefungwa'}
        </div>
      </header>

      <PrizeLadder round={round} />

      <div style={S.actions}>
        <button type="button" style={S.secondaryButton} onClick={() => void autoPick()}>
          Chagua kwa haraka
        </button>
      </div>

      <ol style={S.legs}>
        {round.fixtures.map((fixture, i) => {
          const crowd = round.crowdPicks[i] ?? { home: 0, draw: 0, away: 0 };
          return (
            <li key={fixture.id} style={S.leg}>
              <div style={S.legTitle}>
                <span style={S.legNumber}>{i + 1}</span>
                {fixture.home.name} v {fixture.away.name}
              </div>
              <div style={S.options}>
                {(
                  [
                    ['HOME', '1', crowd.home],
                    ['DRAW', 'X', crowd.draw],
                    ['AWAY', '2', crowd.away],
                  ] as const
                ).map(([value, label, percent]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setLeg(i, value)}
                    aria-pressed={picks[i] === value}
                    style={picks[i] === value ? S.optionSelected : S.option}
                  >
                    <span style={S.optionLabel}>{label}</span>
                    {/*
                      Spec §7: crowd-pick percentages, not odds. In a pool the
                      pick distribution has no effect on our margin, so we can
                      show it truthfully.
                    */}
                    <span style={S.optionPercent}>{percent}%</span>
                  </button>
                ))}
              </div>
            </li>
          );
        })}
      </ol>

      <div style={S.multipliers}>
        {round.allowedMultipliers.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMultiplier(m)}
            aria-pressed={multiplier === m}
            style={multiplier === m ? S.multiplierSelected : S.multiplier}
          >
            {m}x
          </button>
        ))}
      </div>

      {error !== null && <p style={S.error}>{error}</p>}
      {confirmed && <p style={S.success}>Tiketi imenunuliwa. Angalia &quot;Tiketi&quot;.</p>}

      <button
        type="button"
        style={complete && secondsRemaining > 0 ? S.primaryButton : S.primaryButtonDisabled}
        disabled={!complete || busy || secondsRemaining === 0}
        onClick={() => void submit()}
      >
        {busy
          ? 'Inatuma…'
          : `Cheza · ${formatTzs(stakeSenti)} TZS (${picks.filter((p) => p !== null).length}/${round.legs})`}
      </button>
    </main>
  );
}

/**
 * The prize ladder, shown *before* purchase.
 *
 * Spec §7: "The consolation tiers drive the return visit." A player who sees
 * that 8 out of 10 still pays something comes back; one who thinks it is all
 * or nothing does not.
 */
function PrizeLadder({ round }: { round: RoundDetail }): React.JSX.Element {
  return (
    <section style={S.ladder}>
      <h2 style={S.ladderTitle}>Zawadi kwa sasa</h2>
      {round.ladder.map((rung) => (
        <div key={rung.missedLegs} style={S.ladderRow}>
          <span>
            {rung.correctLegs}/{round.legs}
          </span>
          <span style={rung.missedLegs === 0 ? S.ladderTop : undefined}>
            {formatTzs(rung.allocationSenti)} TZS
          </span>
        </div>
      ))}
    </section>
  );
}

/**
 * A countdown that actually moves.
 *
 * Recomputed from the target timestamp on every tick rather than decremented,
 * so a tab that was backgrounded and missed thirty intervals shows the right
 * number immediately instead of running thirty seconds slow forever.
 */
function useCountdown(targetIso: string): number {
  const target = useMemo(() => Date.parse(targetIso), [targetIso]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  return Math.max(0, Math.floor((target - now) / 1000));
}

const base = {
  button: {
    border: '1px solid var(--border)',
    borderRadius: 10,
    background: 'var(--surface)',
    color: 'var(--text)',
    padding: '12px 14px',
    fontSize: 16,
    // 44px is the smallest reliable touch target on a cheap phone.
    minHeight: 44,
    cursor: 'pointer',
  },
} as const;

// `satisfies` rather than an annotation: it validates every entry against
// CSSProperties while keeping the literal keys, so a typo in `S.ladderTop` is a
// compile error instead of an undefined style that silently renders unstyled.
const S = {
  page: { padding: 16, maxWidth: 560, margin: '0 auto' },
  header: { textAlign: 'center', marginBottom: 16 },
  poolLabel: { color: 'var(--muted)', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1 },
  pool: { fontSize: 34, fontWeight: 800, color: 'var(--accent)', lineHeight: 1.1 },
  poolNote: { color: 'var(--muted)', fontSize: 13, marginTop: 4 },
  countdown: { fontVariantNumeric: 'tabular-nums', fontSize: 22, marginTop: 8 },
  countdownUrgent: {
    fontVariantNumeric: 'tabular-nums',
    fontSize: 22,
    marginTop: 8,
    color: 'var(--accent)',
    fontWeight: 700,
  },
  ladder: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 12,
    marginBottom: 16,
  },
  ladderTitle: { margin: '0 0 8px', fontSize: 14, color: 'var(--muted)', fontWeight: 600 },
  ladderRow: { display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 15 },
  ladderTop: { color: 'var(--accent)', fontWeight: 700 },
  actions: { display: 'flex', gap: 8, marginBottom: 12 },
  secondaryButton: { ...base.button, flex: 1 },
  legs: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 },
  leg: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 10,
  },
  legTitle: { fontSize: 14, marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' },
  legNumber: { color: 'var(--muted)', fontSize: 12, minWidth: 18 },
  options: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 },
  option: { ...base.button, display: 'grid', gap: 2, padding: '8px 4px' },
  optionSelected: {
    ...base.button,
    display: 'grid',
    gap: 2,
    padding: '8px 4px',
    background: 'var(--accent)',
    color: '#0b1220',
    borderColor: 'var(--accent)',
    fontWeight: 700,
  },
  optionLabel: { fontSize: 17, fontWeight: 700 },
  optionPercent: { fontSize: 11, opacity: 0.75 },
  multipliers: { display: 'flex', gap: 8, margin: '16px 0' },
  multiplier: { ...base.button, flex: 1 },
  multiplierSelected: {
    ...base.button,
    flex: 1,
    background: 'var(--accent)',
    color: '#0b1220',
    borderColor: 'var(--accent)',
    fontWeight: 700,
  },
  primaryButton: {
    ...base.button,
    width: '100%',
    background: 'var(--win)',
    color: '#06281c',
    borderColor: 'var(--win)',
    fontWeight: 800,
    fontSize: 17,
    minHeight: 52,
  },
  primaryButtonDisabled: {
    ...base.button,
    width: '100%',
    opacity: 0.5,
    cursor: 'not-allowed',
    minHeight: 52,
  },
  error: { color: '#fca5a5', fontSize: 14 },
  success: { color: 'var(--win)', fontSize: 14 },
} satisfies Record<string, React.CSSProperties>;
